import { Box, Divider, Popover, Typography } from '@mui/material';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Dispatch, useEffect } from 'react';
import { useAtomValue } from 'jotai';
import { Business_Profile_Enum } from '@kheops/graphql';
import { getCompanyShortName } from '../common/utils/common.utils';
import { BuyerNavigation, CommonNavigation, SupplierNavigation } from '../common/state/navigation';
import { availableContextsAtom, currentContextAtom } from '../state';
import { brandsAtom } from '../brands/state';
import { ellipsisStyle } from '../common/utils/style.utils';
import CompanyLogo from '../common/components/CompanyLogo';
import NavigationSection from './NavigationSection';
import ContextImage from './ContextImage';
import ContextNavigation from './ContextNavigation';

interface ProfileBoxProps {
  anchorHandler: Dispatch<React.SetStateAction<HTMLElement | null>>
  anchorEl: HTMLElement | null;
}

export default function ProfileBox({ anchorHandler, anchorEl }: ProfileBoxProps): React.JSX.Element {
  const location = useLocation();
  const { t } = useTranslation(['common']);
  const context = useAtomValue(currentContextAtom);
  const availableContexts = useAtomValue(availableContextsAtom);
  const brands = useAtomValue(brandsAtom);
  const navigationItems = context.realm === Business_Profile_Enum.Supplier ? SupplierNavigation : BuyerNavigation;
  const companyBrandLabel = brands.find((brand) => brand.value === context.companyBrand)?.comment;

  const handleUserMenuClose = (): void => {
    anchorHandler(null);
  };

  useEffect((): void => {
    anchorHandler(null);
  }, [location]);

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      onClose={handleUserMenuClose}
      open={Boolean(anchorEl)}
      sx={{
        mt: 1,
        '& .MuiPaper-root': {
          width: 312,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            mb: context.realm === Business_Profile_Enum.Supplier ? 1 : 0,
            mt: context.realm === Business_Profile_Enum.Supplier ? 0.5 : 0,
          }}
        >
          {context.realm === Business_Profile_Enum.Buyer && context.companyBrand && (
            <CompanyLogo
              variant="transparent"
              size="medium"
              brand={context.companyBrand}
              sx={{ width: 48 }}
            />
          )}
          {(context.realm === Business_Profile_Enum.Supplier || !context.companyBrand) && (
            <ContextImage context={context} size={48} borderRadius={20} />
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="titleMedium" sx={{ ...ellipsisStyle }}>
              {getCompanyShortName(context.companyName, context.realm)}
            </Typography>
            {companyBrandLabel && (
              <Typography variant="bodySmall" color="secondary" sx={{ ...ellipsisStyle }}>
                {companyBrandLabel}
              </Typography>
            )}
          </Box>
        </Box>
        <NavigationSection items={navigationItems} />
        <Divider />
      </Box>
      <NavigationSection items={CommonNavigation} />
      {availableContexts.length > 1 && (
        <>
          <Divider />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              mt: 1,
            }}
          >
            <Typography variant="bodySmall" color="secondary" sx={{ my: 1 }}>
              {t('common:other_companies')}
            </Typography>
            <ContextNavigation />
          </Box>
        </>
      )}
    </Popover>
  );
}
